import * as React from 'react';
import type { ValueSnapshot, SelectSearchOption } from 'react-select-search';
import { useSelect } from 'react-select-search';
import type { Distribute, DistributeSingle } from '../../../modules/ts.utils';
import type { TooltipProps } from '../Tooltip/Tooltip';
import Tooltip from '../Tooltip/Tooltip';
import List from '../List/List';
import Checkbox from '../Checkbox/Checkbox';
import ListItem from '../List/ListItem/ListItem';
import Closable from '../Closable';
import cn from '~modules/cn';
import ChevronDownIcon from '../../../assets/icons/v2design/navigation/chevron-down.svg';
import './dropdown.scss';

type ValueProps = {
    tabIndex: number | undefined;
    readOnly?: boolean;
    disabled: boolean;
    onMouseDown: (event: React.MouseEvent | React.TouchEvent) => void;
    onKeyDown: (event: React.KeyboardEvent) => void;
    onKeyUp: (event: React.KeyboardEvent) => void;
    onKeyPress: (event: React.KeyboardEvent) => void;
    onBlur: () => void;
    onFocus: () => void;
    ref: React.MutableRefObject<$TSFixMe>;
};

export type Option<T> = {
    name: string;
    text?: React.ReactNode;
    value: T;
    tip?: string;
    tipOverflow?: boolean;
    leftView?: React.ReactNode;
    rightView?: React.ReactNode;
    disabled?: boolean;
};

type BaseValueSnapshot = Omit<ValueSnapshot, 'highlighted'> & {
    // `react-select-search` incorrectly types highlighted as a boolean, when it actually passes a number
    highlighted: number;
    selectedRef: React.RefObject<HTMLElement>;
};

export type DropdownValueSnapshot<T> = BaseValueSnapshot & {
    option: T;
};

export type DropdownSize = 'medium' | 'small';
export type DropdownWidth = 'full' | 'default';

export type DropdownBaseProps<T> = {
    options?: Option<T>[];
    renderOptions?: Option<T>[];
    placeholder?: string;
    preTextIcon?: React.ReactNode;
    text?: string;
    disabled?: boolean;
    className?: string;
    controlClassName?: string;
    dataTip?: string;
    dataTestId?: string;
    valueTestId?: string;
    dataTipOverflow?: boolean;
    offset?: [number, number];
    menuClassName?: string;
    sameWidth?: boolean;
    menuHost?: TooltipProps['host'];
    useFocusControls?: boolean;
    onFocusChange?: { (state: boolean): void };
    defaultItemsSize?: boolean;
    defaultListChildren?: React.ReactNode;
    appendParent?: boolean;
    size?: DropdownSize;
    width?: DropdownWidth;
};

export type DropdownSingleProps<T> = {
    multiple?: false;
    onChange?: (value: T) => void;
    value: T;
    renderTitle?: { (value: T): React.ReactNode | undefined };
    children?: (snapshot: DropdownValueSnapshot<Option<T>>, optionProps: OptionProps) => JSX.Element;
};

export type DropdownMultipleProps<T> = {
    multiple: true;
    onChange?: (value: Distribute<T>) => void;
    showCheckbox?: boolean;
    value: Distribute<T>;
    renderTitle?: { (value: Distribute<T>): React.ReactNode | undefined };
    children?: (snapshot: DropdownValueSnapshot<Option<DistributeSingle<T>>>, optionProps: OptionProps) => JSX.Element;
};

export type DropdownProps<T> = DropdownBaseProps<T> & (DropdownSingleProps<T> | DropdownMultipleProps<T>);

export type OptionProps = ValueProps;

export const defaultProps: DropdownProps<string> = {
    disabled: false,
    value: '',
    useFocusControls: true,
    defaultItemsSize: true,
    multiple: false,
    size: 'medium',
    width: 'default'
};

// This component renders the drop down "selected value" and "trigger" element, what's visible when the "dropdown" is closed.
// This component is used to follow by a tooltip/menu component that's opened when the "dropdown" is clicked, the ref field connects them.
// for more info see storybook's dropdown stories
const Dropdown = <T extends string | number>(props: DropdownProps<T>): JSX.Element => {
    const {
        sameWidth,
        preTextIcon,
        disabled,
        className,
        dataTip,
        dataTipOverflow,
        dataTestId,
        children,
        value,
        multiple,
        offset,
        onChange,
        menuClassName,
        placeholder,
        useFocusControls,
        renderTitle,
        defaultItemsSize,
        controlClassName,
        size,
        width
    } = {
        ...defaultProps,
        ...props
    } as DropdownProps<T>;
    const options = React.useMemo(() => props.options ?? [{ value, name: value }], [props.options, value]);
    const [snapshot, valueProps, optionProps] = useSelect({
        options: options as SelectSearchOption[],
        value: value as $TSFixMe,
        multiple,
        disabled,
        onChange: (changedValue: $TSFixMe) => {
            onChange?.(changedValue);
        }
    });
    const actionRef = React.useRef<HTMLInputElement>(null);
    const selectedItemRef = React.useRef<HTMLElement>(null);
    const isFocusFiredRef = React.useRef(false);

    React.useEffect(() => {
        const offsetParent = selectedItemRef.current?.offsetParent as HTMLElement | undefined;
        if (offsetParent && selectedItemRef.current) {
            const itemHeight = selectedItemRef.current.offsetHeight;
            const centerPos = selectedItemRef.current.offsetTop - (offsetParent.offsetHeight / 2 - itemHeight / 2);
            offsetParent.scrollTop = centerPos;
        }
    }, [snapshot.focus]);

    const showMultipleCheckbox = props.multiple && (props.showCheckbox ?? true);
    const childrenSnapshot = { ...snapshot, selectedRef: selectedItemRef } as $TSFixMe;
    const hasChildrenFn = typeof children === 'function';

    const renderOptions = props.renderOptions ?? snapshot.options;

    let renderedTitle: React.ReactNode | undefined;
    if (renderTitle) {
        renderedTitle = renderTitle(value as $TSFixMe);
    }
    if (typeof renderedTitle === 'undefined') {
        renderedTitle = snapshot.displayValue || placeholder;
    }

    // safari not firing focus for button and useSelect relies on focus state - so using div instead of button
    return (
        <div
            className={cn(`dropdown`, `dropdown-size-${size}`, `dropdown-width-${width}`, className)}
            ref={actionRef}
            data-tip={dataTip}
            data-tip-overflow={dataTipOverflow ? 'dropdown-control__text' : undefined}
        >
            <div
                className={cn('dropdown-control', controlClassName, { 'dropdown-control--selected': snapshot.focus })}
                {...(valueProps as unknown as ValueProps)}
                tabIndex={0}
                onFocus={() => {
                    if (useFocusControls && !disabled) {
                        isFocusFiredRef.current = true;
                        valueProps.onFocus();
                        props.onFocusChange?.(true);
                    }
                }}
                onBlur={() => {
                    if (useFocusControls) {
                        isFocusFiredRef.current = false;

                        if (snapshot.value) {
                            valueProps.onBlur();
                            props.onFocusChange?.(false);
                        } else {
                            // blur is similar to e.stopPropagation
                            // hence if option has event listeners - they are not triggered
                            // allow options which have empty values to use custom listeners
                            setTimeout(() => {
                                valueProps.onBlur();
                                props.onFocusChange?.(false);
                            }, 100);
                        }
                    }
                }}
                onClick={(e) => {
                    if (useFocusControls) {
                        if (!disabled && document.activeElement === e.currentTarget) {
                            if (isFocusFiredRef.current) {
                                isFocusFiredRef.current = false;
                            } else {
                                e.currentTarget.blur(); // click again on focused
                            }
                        }
                    } else {
                        if (snapshot.focus) {
                            valueProps.onBlur();
                            props.onFocusChange?.(false);
                        } else if (!disabled) {
                            valueProps.onFocus();
                            props.onFocusChange?.(true);
                        }
                    }
                }}
                data-testid={dataTestId}
            >
                {preTextIcon}
                <span className="dropdown-control__text" data-testid={props.valueTestId}>
                    {renderedTitle}
                </span>
                <ChevronDownIcon className="dropdown-control__icon" />
            </div>
            {snapshot.focus && (
                <Tooltip
                    className={cn('dropdown-tooltip', menuClassName, { 'dropdown-tooltip--default-size': defaultItemsSize })}
                    for={actionRef}
                    appearanceClassNames={[
                        'tooltip--no-background',
                        'tooltip--no-max-width',
                        'tooltip--box-shadow',
                        'tooltip--no-padding',
                        'tooltip--default-max-height'
                    ]}
                    tooltipProps={{
                        trigger: 'click',
                        visible: snapshot.focus
                    }}
                    offset={offset}
                    sameWidth={sameWidth}
                    host={props.menuHost}
                    appendParent={props.appendParent}
                >
                    <Closable
                        onClose={() => {
                            if (!useFocusControls) {
                                valueProps.onBlur();
                                props.onFocusChange?.(false);
                            }
                        }}
                        includeElements={[valueProps.ref]}
                    >
                        {hasChildrenFn && (
                            <>
                                {children?.(childrenSnapshot, {
                                    ...(optionProps as unknown as OptionProps)
                                })}
                            </>
                        )}
                        {!hasChildrenFn && (
                            <div className="dropdown-control__menu-container">
                                {props.defaultListChildren}
                                {renderOptions.length > 0 && (
                                    <List containerClassName="dropdown-control__list-container">
                                        {renderOptions.map((option, index) => {
                                            const dropdownOption = option as unknown as Option<T>;
                                            const isSelected = props.multiple
                                                ? !!(snapshot as unknown as DropdownValueSnapshot<Option<T>[]>).option?.find(
                                                      (dropdownOption) => dropdownOption.value === option.value
                                                  )
                                                : (snapshot as unknown as DropdownValueSnapshot<Option<T>>).option?.value === option.value;

                                            const isHighlighted = (snapshot as unknown as DropdownValueSnapshot<Option<T>>).highlighted === index;

                                            return (
                                                <ListItem
                                                    {...(optionProps as unknown as OptionProps)}
                                                    Control="button"
                                                    highlighted={isHighlighted}
                                                    selected={isSelected}
                                                    key={option.value}
                                                    value={option.value as string}
                                                    text={dropdownOption.text || option.name}
                                                    leftView={
                                                        (dropdownOption.leftView || showMultipleCheckbox) && (
                                                            <>
                                                                {showMultipleCheckbox && (
                                                                    <Checkbox className="dropdown-control__checkbox" checked={isSelected} readOnly />
                                                                )}
                                                                {dropdownOption.leftView}
                                                            </>
                                                        )
                                                    }
                                                    rightView={dropdownOption.rightView}
                                                    size="full"
                                                    setRef={isSelected ? selectedItemRef : undefined}
                                                    dataTip={dropdownOption.tip}
                                                    dataTipOverflow={dropdownOption.tipOverflow}
                                                    disabled={dropdownOption.disabled}
                                                />
                                            );
                                        })}
                                    </List>
                                )}
                            </div>
                        )}
                    </Closable>
                </Tooltip>
            )}
        </div>
    );
};

Dropdown.displayName = 'Dropdown';

export default Dropdown;
